<template>
  <div class="typing-game" :class="{ collapsed: isStatsCollapsed }">
    <div class="game-header"></div>

    <div
      class="game-sidebar"
      v-if="gameStarted"
      :class="{ collapsed: isStatsCollapsed }"
    >
      <div class="collapse-toggle" @click="toggleStats">
        <span class="toggle-icon">{{ isStatsCollapsed ? "◀" : "▶" }}</span>
      </div>

      <div class="stats-container">
        <div class="sidebar-header">
          <h1>{{ title }}</h1>
          <div class="divider"></div>
        </div>

        <div class="stat-box score">
          <div class="stat-icon">🎯</div>
          <div class="stat-content">
            <div class="stat-value">{{ score }}</div>
            <div class="stat-label">得分</div>
          </div>
        </div>
        <div class="stat-box accuracy">
          <div class="stat-icon">✨</div>
          <div class="stat-content">
            <div class="stat-value">{{ accuracy }}%</div>
            <div class="stat-label">正确率</div>
          </div>
        </div>
        <div class="stat-box level">
          <div class="stat-icon">📈</div>
          <div class="stat-content">
            <div class="stat-value">{{ currentLevel }}/{{ levels.length }}</div>
            <div class="stat-label">{{ levelName }}</div>
          </div>
        </div>
        <div class="divider"></div>
        <div class="control-buttons">
          <button class="game-button guide-btn" @click="showGuideModal">
            <span class="button-icon">🔤</span>
            指法说明
          </button>

          <button class="game-button guide-btn" @click="showHistory = true">
            <span class="button-icon">📜</span>
            历史记录
          </button>

          <button class="game-button guide-btn" @click="restartGame">
            <span class="button-icon">🔄</span>
            重新开始
          </button>
        </div>
        <button class="exit-button" @click="exitPractice">
          <span class="button-icon">🚪</span>
          返回上一页
        </button>
      </div>
    </div>

    <div class="game-display" :class="{ correct: isCorrect, wrong: isWrong }">
      <div class="mini-progress-container">
        <div class="mini-progress-info">
          <span class="level-name">{{ levels[currentLevel - 1].name }}</span>
          <span class="progress-percent">{{ Math.round(progress) }}%</span>
        </div>
        <div class="mini-progress">
          <div class="mini-progress-bar" :style="{ width: progress + '%' }">
            <div class="mini-progress-glow"></div>
          </div>
        </div>
      </div>
      <div class="target-letter">{{ currentLetter }}</div>
      <div class="finger-hint" v-if="fingerPositions[currentLetter]">
        请用{{ getFinger(fingerPositions[currentLetter].finger) }}
        {{ getRowDirection(fingerPositions[currentLetter].row) }}按键
      </div>
    </div>

    <div class="typing-guide">
      <div class="hands-container">
        <div class="hand left-hand">
          <div class="finger-container">
            <div
              class="finger pinky"
              :class="{ active: fingerMap[currentLetter] === 'LP' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LP'"
              ></div>
              小指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger ring"
              :class="{ active: fingerMap[currentLetter] === 'LR' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LR'"
              ></div>
              无名指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger middle"
              :class="{ active: fingerMap[currentLetter] === 'LM' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LM'"
              ></div>
              中指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger index"
              :class="{ active: fingerMap[currentLetter] === 'LI' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'LI'"
              ></div>
              食指
            </div>
          </div>
        </div>
        <div class="hand right-hand">
          <div class="finger-container">
            <div
              class="finger index"
              :class="{ active: fingerMap[currentLetter] === 'RI' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RI'"
              ></div>
              食指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger middle"
              :class="{ active: fingerMap[currentLetter] === 'RM' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RM'"
              ></div>
              中指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger ring"
              :class="{ active: fingerMap[currentLetter] === 'RR' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RR'"
              ></div>
              无名指
            </div>
          </div>
          <div class="finger-container">
            <div
              class="finger pinky"
              :class="{ active: fingerMap[currentLetter] === 'RP' }"
            >
              <div
                class="finger-track"
                v-show="fingerMap[currentLetter] === 'RP'"
              ></div>
              小指
            </div>
          </div>
        </div>
      </div>

      <!-- 练习区键盘 -->
      <div class="practice-keyboard">
        <div class="second-led"></div>
        <div class="third-led"></div>
        <div class="keyboard-frame">
          <!-- 数字键行 -->
          <div class="practice-row number-row">
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">~</span>
                <span class="dual-key-main">`</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">!</span>
                <span class="dual-key-main">1</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">@</span>
                <span class="dual-key-main">2</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">#</span>
                <span class="dual-key-main">3</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">$</span>
                <span class="dual-key-main">4</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">%</span>
                <span class="dual-key-main">5</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">^</span>
                <span class="dual-key-main">6</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">&</span>
                <span class="dual-key-main">7</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">*</span>
                <span class="dual-key-main">8</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">(</span>
                <span class="dual-key-main">9</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">)</span>
                <span class="dual-key-main">0</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">_</span>
                <span class="dual-key-main">-</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">+</span>
                <span class="dual-key-main">=</span>
              </div>
            </div>
            <div class="practice-key special-key backspace">
              <span class="special-key-text">⌫</span>
            </div>
          </div>

          <!-- 第一行 -->
          <div class="practice-row top-row">
            <div class="practice-key special-key tab">
              <span class="special-key-text">Tab</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'Q' }"
            >
              <span class="single-key">Q</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'W' }"
            >
              <span class="single-key">W</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'E' }"
            >
              <span class="single-key">E</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'R' }"
            >
              <span class="single-key">R</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'T' }"
            >
              <span class="single-key">T</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'Y' }"
            >
              <span class="single-key">Y</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'U' }"
            >
              <span class="single-key">U</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'I' }"
            >
              <span class="single-key">I</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'O' }"
            >
              <span class="single-key">O</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'P' }"
            >
              <span class="single-key">P</span>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">{</span>
                <span class="dual-key-main">[</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">}</span>
                <span class="dual-key-main">]</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">|</span>
                <span class="dual-key-main">\</span>
              </div>
            </div>
          </div>

          <!-- 第二行 -->
          <div class="practice-row home-row">
            <div class="practice-key special-key caps">
              <span class="special-key-text">Caps</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'A' }"
            >
              <span class="single-key">A</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'S' }"
            >
              <span class="single-key">S</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'D' }"
            >
              <span class="single-key">D</span>
            </div>
            <div
              class="practice-key home-key"
              :class="{ active: currentLetter === 'F' }"
            >
              <span class="single-key">F</span>
              <span class="key-bump"></span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'G' }"
            >
              <span class="single-key">G</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'H' }"
            >
              <span class="single-key">H</span>
            </div>
            <div
              class="practice-key home-key"
              :class="{ active: currentLetter === 'J' }"
            >
              <span class="single-key">J</span>
              <span class="key-bump"></span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'K' }"
            >
              <span class="single-key">K</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'L' }"
            >
              <span class="single-key">L</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === ';' }"
            >
              <div class="key-content">
                <span class="dual-key-sub">:</span>
                <span class="dual-key-main">;</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">"</span>
                <span class="dual-key-main">'</span>
              </div>
            </div>
            <div class="practice-key special-key enter">
              <span class="special-key-text">Enter</span>
            </div>
          </div>

          <!-- 第三行 -->
          <div class="practice-row bottom-row">
            <div class="practice-key special-key shift">
              <span class="special-key-text">Shift</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'Z' }"
            >
              <span class="single-key">Z</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'X' }"
            >
              <span class="single-key">X</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'C' }"
            >
              <span class="single-key">C</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'V' }"
            >
              <span class="single-key">V</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'B' }"
            >
              <span class="single-key">B</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'N' }"
            >
              <span class="single-key">N</span>
            </div>
            <div
              class="practice-key"
              :class="{ active: currentLetter === 'M' }"
            >
              <span class="single-key">M</span>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">&lt;</span>
                <span class="dual-key-main">,</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">&gt;</span>
                <span class="dual-key-main">.</span>
              </div>
            </div>
            <div class="practice-key">
              <div class="key-content">
                <span class="dual-key-sub">?</span>
                <span class="dual-key-main">/</span>
              </div>
            </div>
            <div class="practice-key special-key shift right-shift">
              <span class="special-key-text">Shift</span>
            </div>
          </div>

          <!-- 第四行 (空格键) -->
          <div class="practice-row space-row">
            <div class="practice-key special-key ctrl">
              <span class="special-key-text">Ctrl</span>
            </div>
            <div class="practice-key special-key fn">
              <span class="special-key-text">Fn</span>
            </div>
            <div class="practice-key special-key win">
              <span class="special-key-text">Win</span>
            </div>
            <div class="practice-key special-key alt">
              <span class="special-key-text">Alt</span>
            </div>
            <div class="practice-key special-key space">
              <span class="special-key-text"></span>
            </div>
            <div class="practice-key special-key alt-gr">
              <span class="special-key-text">Alt</span>
            </div>
            <div class="practice-key special-key menu">
              <span class="special-key-text">Menu</span>
            </div>
            <div class="practice-key special-key ctrl">
              <span class="special-key-text">Ctrl</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加打字报告弹窗 -->
    <div class="report-modal" v-if="showReport">
      <div class="report-content">
        <h2>打字练习报告</h2>
        <div class="report-stats">
          <div class="stat-item">
            <div class="stat-label">练习用时</div>
            <div class="stat-value">{{ formatTime(totalTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">最终得分</div>
            <div class="stat-value">{{ score }}分</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">正确率</div>
            <div class="stat-value">{{ accuracy }}%</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">打字速度</div>
            <div class="stat-value">{{ typingSpeed }}个/分钟</div>
          </div>
        </div>

        <div class="report-message">
          <div class="result-container">
            <img :src="resultImage" :alt="resultMessage" />
            <p>{{ resultMessage }}</p>
          </div>
        </div>

        <div class="report-buttons">
          <button @click="restartGame">关闭</button>
        </div>
      </div>
    </div>

    <!-- 添加历史记录弹窗 -->
    <div class="history-modal" v-if="showHistory">
      <div class="history-content">
        <div class="history-header">
          <div class="history-title-icon">📚</div>
          <h2>练习历史记录</h2>
        </div>
        <div class="history-decoration top-left"></div>
        <div class="history-decoration top-right"></div>
        <div class="history-decoration bottom-left"></div>
        <div class="history-decoration bottom-right"></div>

        <div class="history-list" v-if="historyRecords.length">
          <div
            class="history-item"
            v-for="(record, index) in historyRecords"
            :key="index"
          >
            <div class="history-item-header">
              <div class="history-date">
                <span class="date-icon">🗓️</span>
                {{ formatDate(record.date) }}
              </div>
              <div class="history-badge">
                第{{ historyRecords.length - index }}次
              </div>
            </div>
            <div class="history-details">
              <div class="detail-item">
                <div class="detail-icon">⏱️</div>
                <span class="label">用时</span>
                <span class="value">{{ formatTime(record.totalTime) }}</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">🎯</div>
                <span class="label">得分</span>
                <span class="value">{{ record.score }}分</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">✨</div>
                <span class="label">正确率</span>
                <span class="value">{{ record.accuracy }}%</span>
              </div>
              <div class="detail-item">
                <div class="detail-icon">⚡</div>
                <span class="label">速度</span>
                <span class="value">{{ record.typingSpeed }}个/分钟</span>
              </div>
            </div>
          </div>
        </div>
        <div class="no-history" v-else>
          <div class="no-history-icon">🔍</div>
          <p>还没有练习记录哦，开始练习吧！</p>
          <div class="no-history-decoration"></div>
        </div>
        <div class="history-buttons">
          <button
            class="history-button clear-button"
            @click="clearHistory"
            v-if="historyRecords.length"
          >
            <span class="button-icon">🗑️</span>
            清空记录
          </button>
          <button
            class="history-button close-button"
            @click="showHistory = false"
          >
            <span class="button-icon">✖️</span>
            关闭
          </button>
        </div>
      </div>
    </div>

    <!-- 基准键位指导弹窗改为默认显示 -->
    <div class="guide-modal" v-if="showGuide">
      <div class="guide-content">
        <div class="guide-header">
          <div class="guide-title-icon">⌨️</div>
          <h2>键盘指法说明</h2>
          <button class="guide-close-btn" @click="showGuide = false">✖</button>
        </div>

        <div class="guide-decoration top-left"></div>
        <div class="guide-decoration top-right"></div>
        <div class="guide-decoration bottom-left"></div>
        <div class="guide-decoration bottom-right"></div>

        <p class="guide-subtitle">掌握正确的指法，提高打字速度和准确性</p>

        <!-- 键盘布局部分 -->
        <div class="guide-keyboard">
          <div class="guide-row">
            <div class="guide-key" data-finger="LP">Q</div>
            <div class="guide-key" data-finger="LR">W</div>
            <div class="guide-key" data-finger="LM">E</div>
            <div class="guide-key" data-finger="LI">R</div>
            <div class="guide-key" data-finger="LI">T</div>
            <div class="guide-key" data-finger="RI">Y</div>
            <div class="guide-key" data-finger="RI">U</div>
            <div class="guide-key" data-finger="RM">I</div>
            <div class="guide-key" data-finger="RR">O</div>
            <div class="guide-key" data-finger="RP">P</div>
          </div>
          <div class="guide-row home-row">
            <div class="guide-key base-key" data-finger="LP">A</div>
            <div class="guide-key base-key" data-finger="LR">S</div>
            <div class="guide-key base-key" data-finger="LM">D</div>
            <div class="guide-key base-key bump" data-finger="LI">F</div>
            <div class="guide-key" data-finger="LI">G</div>
            <div class="guide-key" data-finger="RI">H</div>
            <div class="guide-key base-key bump" data-finger="RI">J</div>
            <div class="guide-key base-key" data-finger="RM">K</div>
            <div class="guide-key base-key" data-finger="RR">L</div>
          </div>
          <div class="guide-row">
            <div class="guide-key" data-finger="LP">Z</div>
            <div class="guide-key" data-finger="LR">X</div>
            <div class="guide-key" data-finger="LM">C</div>
            <div class="guide-key" data-finger="LI">V</div>
            <div class="guide-key" data-finger="LI">B</div>
            <div class="guide-key" data-finger="RI">N</div>
            <div class="guide-key" data-finger="RI">M</div>
          </div>
        </div>

        <!-- 手指颜色图例 -->
        <div class="finger-legend">
          <div class="legend-item">
            <div class="legend-color" style="background: #FF4081;"></div>
            <div class="legend-text">小指</div>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #7C4DFF;"></div>
            <div class="legend-text">无名指</div>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #00BCD4;"></div>
            <div class="legend-text">中指</div>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #FFC107;"></div>
            <div class="legend-text">食指</div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="guide-tips">
          <div class="tip-card">
            <div class="tip-icon">🎯</div>
            <div class="tip-title">基准位置</div>
            <p>左手: A S D F</p>
            <p>右手: J K L ;</p>
          </div>
          <div class="tip-card">
            <div class="tip-icon">💡</div>
            <div class="tip-title">小技巧</div>
            <p>F 和 J 键上有凸点</p>
            <p>帮助盲打定位</p>
          </div>
          <div class="tip-card">
            <div class="tip-icon">✨</div>
            <div class="tip-title">记忆方法</div>
            <p>每个手指负责固定区域</p>
            <p>保持手指自然弯曲</p>
          </div>
        </div>

        <!-- 按钮区域 -->
        <div class="guide-buttons">
          <button @click="showGuide = false" class="guide-button secondary">
            <span class="button-icon">✖️</span>
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TypingGame-1",
  data() {
    return {
      title: "字母练习",
      gameStarted: false,
      currentLetter: "",
      score: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      accuracy: 100,
      isCorrect: false,
      isWrong: false,
      currentLevel: 1,
      progress: 0,
      levels: [
        {
          name: "基准键位",
          letters: "ASDFGHJKL",
          description: "请将左手四指放在ASDF，右手四指放在JKL;上",
        },
        {
          name: "向上扩展",
          letters: "QWERUIOP",
          description: "练习键盘上方的字母",
        },
        {
          name: "向下扩展",
          letters: "ZXCVBNM",
          description: "练习键盘下方的字母",
        },
        {
          name: "全键盘",
          letters: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
          description: "练习所有字母",
        },
      ],
      fingerPositions: {
        A: { finger: "LP", row: "home" },
        S: { finger: "LR", row: "home" },
        D: { finger: "LM", row: "home" },
        F: { finger: "LI", row: "home" },
        G: { finger: "LI", row: "home" },
        H: { finger: "RI", row: "home" },
        J: { finger: "RI", row: "home" },
        K: { finger: "RM", row: "home" },
        L: { finger: "RR", row: "home" },
        ";": { finger: "RP", row: "home" },

        Q: { finger: "LP", row: "top" },
        W: { finger: "LR", row: "top" },
        E: { finger: "LM", row: "top" },
        R: { finger: "LI", row: "top" },
        T: { finger: "LI", row: "top" },
        Y: { finger: "RI", row: "top" },
        U: { finger: "RI", row: "top" },
        I: { finger: "RM", row: "top" },
        O: { finger: "RR", row: "top" },
        P: { finger: "RP", row: "top" },

        Z: { finger: "LP", row: "bottom" },
        X: { finger: "LR", row: "bottom" },
        C: { finger: "LM", row: "bottom" },
        V: { finger: "LI", row: "bottom" },
        B: { finger: "LI", row: "bottom" },
        N: { finger: "RI", row: "bottom" },
        M: { finger: "RI", row: "bottom" },
      },
      fingerMap: {
        Q: "LP",
        A: "LP",
        Z: "LP",
        W: "LR",
        S: "LR",
        X: "LR",
        E: "LM",
        D: "LM",
        C: "LM",
        R: "LI",
        F: "LI",
        V: "LI",
        T: "LI",
        G: "LI",
        B: "LI",
        Y: "RI",
        H: "RI",
        N: "RI",
        U: "RI",
        J: "RI",
        M: "RI",
        I: "RM",
        K: "RM",
        O: "RR",
        L: "RR",
        P: "RP",
        ";": "RP",
      },
      showReport: false,
      startTime: null,
      totalTime: 0,
      typingSpeed: 0,
      resultMessage: "",
      resultImage: "",
      resultImages: {
        excellent: "https://wugecdn.steam.fun/typefun/images/excellent.png",
        good: "https://wugecdn.steam.fun/typefun/images/good.png",
        keepTrying: "https://wugecdn.steam.fun/typefun/images/keep-trying.png",
      },
      showHistory: false,
      historyRecords: [],
      maxHistoryRecords: 10, // 最多保存10条记录
      showGuide: false,
      isStatsCollapsed: false,
    };
  },
  computed: {
    levelName() {
      return this.levels[this.currentLevel - 1].name;
    },
  },
  methods: {
    startGame() {
      this.showGuide = false;
      this.gameStarted = true;
      this.generateNewLetter();
      window.addEventListener("keydown", this.handleKeyPress);
      this.startTime = new Date();
      this.showReport = false;
    },
    restartGame() {
      this.score = 0;
      this.totalAttempts = 0;
      this.correctAttempts = 0;
      this.accuracy = 100;
      this.currentLevel = 1;
      this.progress = 0;
      this.showReport = false;
      this.showGuide = false;
      this.gameStarted = false;

      // 确保移除之前的事件监听
      window.removeEventListener("keydown", this.handleKeyPress);

      // 直接重新开始游戏
      this.startGame();
    },
    generateNewLetter() {
      const currentLevelLetters = this.levels[this.currentLevel - 1].letters;
      let newLetter;
      do {
        const randomIndex = Math.floor(
          Math.random() * currentLevelLetters.length
        );
        newLetter = currentLevelLetters[randomIndex];
      } while (newLetter === this.currentLetter); // 避免连续出现相同字母
      this.currentLetter = newLetter;
    },
    handleKeyPress(event) {
      if (!this.gameStarted) return;

      const pressedKey = event.key.toUpperCase();
      this.totalAttempts++;

      if (pressedKey === this.currentLetter) {
        this.handleCorrectKey();
      } else {
        this.handleWrongKey();
      }

      this.accuracy = Math.round(
        (this.correctAttempts / this.totalAttempts) * 100
      );
    },
    handleCorrectKey() {
      this.isCorrect = true;
      this.correctAttempts++;
      this.score += 10;
      this.progress += 5;

      if (this.progress >= 100) {
        this.levelUp();
      }

      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/correct.mp3"
      );
      audio.play();

      setTimeout(() => {
        this.isCorrect = false;
        this.generateNewLetter();
      }, 300);
    },
    handleWrongKey() {
      this.isWrong = true;
      this.score = Math.max(0, this.score - 5);

      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/fail.mp3"
      );
      audio.play();

      setTimeout(() => {
        this.isWrong = false;
      }, 300);
    },
    levelUp() {
      if (this.currentLevel < this.levels.length) {
        this.currentLevel++;
        this.progress = 0;
      } else {
        this.generateReport();
      }
    },
    generateReport() {
      // 移除键盘事件监听
      window.removeEventListener("keydown", this.handleKeyPress);

      const endTime = new Date();
      this.totalTime = (endTime - this.startTime) / 1000;
      this.typingSpeed = Math.round(
        this.correctAttempts / (this.totalTime / 60)
      );

      // 生成报告数据
      const reportData = {
        date: new Date().toISOString(),
        totalTime: this.totalTime,
        score: this.score,
        accuracy: this.accuracy,
        typingSpeed: this.typingSpeed,
      };

      // 保存到历史记录
      this.saveToHistory(reportData);

      // 设置结果信息
      if (this.score >= 300 && this.accuracy >= 90) {
        this.resultMessage = "太棒了！你是打字小能手！";
        this.resultImage = this.resultImages.excellent;
      } else if (this.score >= 200 && this.accuracy >= 80) {
        this.resultMessage = "做得不错！继续加油！";
        this.resultImage = this.resultImages.good;
      } else {
        this.resultMessage = "慢慢来，相信你会做得更好！";
        this.resultImage = this.resultImages.keepTrying;
      }

      // 显示报告并停止游戏状态
      this.showReport = true;
      this.gameStarted = false;

      // 重置当前字母
      this.currentLetter = "";
    },
    saveToHistory(record) {
      // 从 localStorage 获取现有记录
      const records = JSON.parse(localStorage.getItem("typingHistory") || "[]");

      // 添加新记录到开头
      records.unshift(record);

      // 只保留最新的 maxHistoryRecords 条记录
      this.historyRecords = records.slice(0, this.maxHistoryRecords);

      // 保存到 localStorage
      localStorage.setItem(
        "typingHistory",
        JSON.stringify(this.historyRecords)
      );
    },
    clearHistory() {
      if (confirm("确定要清空所有记录吗？")) {
        this.historyRecords = [];
        localStorage.removeItem("typingHistory");
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")} ${String(
        date.getHours()
      ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}分${remainingSeconds}秒`;
    },
    closeReport() {
      this.showReport = false;
      // 直接重新开始游戏，不显示欢迎界面
      this.startGame();
    },
    getFinger(finger) {
      const fingerMap = {
        LP: "小指",
        LR: "无名指",
        LM: "中指",
        LI: "食指",
        RI: "食指",
        RM: "中指",
        RR: "无名指",
        RP: "小指",
      };
      return fingerMap[finger];
    },
    getRowDirection(row) {
      const rowMap = {
        home: "",
        top: "上方",
        bottom: "下方",
      };
      return rowMap[row];
    },
    backToMap() {
      this.$router.push("/");
    },
    toggleStats() {
      this.isStatsCollapsed = !this.isStatsCollapsed;
    },
    exitPractice() {
      // 返回上一路由
      this.$router.go(-1);
    },
    showGuideModal() {
      // 显示指法说明弹窗
      this.showGuide = true;
    },
  },
  mounted() {
    // 页面加载时从 localStorage 读取历史记录
    this.historyRecords = JSON.parse(
      localStorage.getItem("typingHistory") || "[]"
    );

    // 直接启动游戏，跳过欢迎界面
    this.startGame();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyPress);
  },
  watch: {
    score() {
      const scoreBox = document.querySelector(".stat-box.score .stat-value");
      if (scoreBox) {
        scoreBox.style.animation = "none";
        scoreBox.offsetHeight; // 触发重绘
        scoreBox.style.animation = "scoreChange 0.3s ease-out";
      }
    },
  },
};
</script>

<style scoped>
.typing-game {
  padding-right: var(--sidebar-width);
  padding-left: 20px;
  transition: padding-right 0.3s ease;
}

.typing-game.collapsed {
  padding-right: 20px;
}

.game-header {
  display: none;
}

.game-sidebar {
  --sidebar-width: 200px;
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: var(--sidebar-width);
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px 0 0 16px;
  box-shadow: -4px 0 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  overflow: hidden;
  z-index: 100;
}

.game-sidebar.collapsed {
  width: 24px;
  background: rgba(255, 255, 255, 0.9);
}

.collapse-toggle {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 0 4px 4px 0;
  transition: all 0.3s ease;
  z-index: 101;
}

.collapse-toggle:hover {
  background: rgba(33, 150, 243, 0.2);
}

.toggle-icon {
  font-size: 12px;
  color: #2196f3;
  transition: transform 0.3s ease;
}

.collapsed .toggle-icon {
  transform: rotate(180deg);
}

.stats-container {
  padding: 15px 15px 15px 30px;
  opacity: 1;
  transform: translateX(0);
  transition: all 0.3s ease;
}

.collapsed .stats-container {
  opacity: 0;
  transform: translateX(100%);
}

.sidebar-header {
  text-align: center;
  padding-bottom: 15px;
}

.sidebar-header h1 {
  font-size: 20px;
  color: #2196f3;
  margin: 0;
  font-weight: 600;
}

.divider {
  height: 2px;
  background: linear-gradient(to right, transparent, #2196f3, transparent);
  margin: 15px 0;
  opacity: 0.3;
}

.stats-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.stat-box {
  position: relative;
  padding: 12px 15px;
  border-radius: 10px;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 10px;
}

.stat-box:hover {
  transform: translateX(-8px);
}

.stat-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.3)
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.stat-box:hover::before {
  opacity: 1;
}

.stat-icon {
  width: 36px;
  height: 36px;
  font-size: 18px;
  border-radius: 8px;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.stat-content .stat-value {
  font-size: 24px;
  font-weight: 700;
  white-space: nowrap;
  text-align: center;
  margin-bottom: 2px;
  color: inherit;
  text-shadow: none;
  display: block;
}

.stat-content .stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
  text-align: center;
  font-weight: normal;
}

.stat-content .stat-label::before {
  display: none;
}

.stat-label {
  font-size: 15px;
  color: #666;
  position: relative;
  display: inline-block;
  font-weight: 600;
}

.stat-label::before {
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.game-display {
  margin-top: 30px;
  text-align: center;
  padding: 30px 40px 40px;
  margin: 20px 0;
  border-radius: 20px;
  background: linear-gradient(145deg, #ffffff, #f8f9ff);
  transition: all 0.3s;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08),
    inset 0 -4px 0 rgba(0, 0, 0, 0.02);
  border: 2px solid rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
}

/* 添加精致小巧的进度条样式 */
.mini-progress-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 12px 20px 10px;
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 18px 18px 0 0;
  z-index: 5;
}

.mini-progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.level-name {
  font-size: 14px;
  font-weight: 600;
  color: #4a6fff;
}

.progress-percent {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background: rgba(74, 111, 255, 0.1);
  padding: 2px 8px;
  border-radius: 10px;
}

.mini-progress {
  height: 6px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
  overflow: hidden;
}

.mini-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4a6fff, #9c4aff);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mini-progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%
  );
  animation: progressGlow 2s linear infinite;
}

@keyframes progressGlow {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.game-display.correct .mini-progress-bar {
  background: linear-gradient(90deg, #4caf50, #8bc34a);
}

.game-display.wrong .mini-progress-bar {
  background: linear-gradient(90deg, #f44336, #ff9800);
}

/* 调整 target-letter 的上边距，为进度条腾出空间 */
.target-letter {
  font-size: 100px;
  font-weight: bold;
  margin: 40px 0 15px;
  color: #333;
  position: relative;
  z-index: 1;
  text-shadow: 3px 3px 0 rgba(0, 0, 0, 0.1), 0 0 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  animation: letterFloat 3s infinite ease-in-out;
  display: inline-block;
}

@keyframes letterFloat {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0);
  }
}

.game-display.correct .target-letter {
  color: #4caf50;
  animation: letterPop 0.5s ease-out;
}

.game-display.wrong .target-letter {
  color: #f44336;
  animation: letterShake 0.5s ease-in-out;
}

@keyframes letterPop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes letterShake {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  50% {
    transform: translateX(10px);
  }
  75% {
    transform: translateX(-5px);
  }
  100% {
    transform: translateX(0);
  }
}

.finger-hint {
  font-size: 18px;
  color: #555;
  background: rgba(255, 255, 255, 0.7);
  padding: 10px 20px;
  border-radius: 30px;
  display: inline-block;
  margin-top: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0.05);
  font-weight: 500;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .game-display {
    padding: 30px 20px;
  }

  .target-letter {
    font-size: 80px;
  }

  .finger-hint {
    font-size: 16px;
    padding: 8px 16px;
  }
}

@media (max-width: 576px) {
  .game-display {
    padding: 20px 15px;
  }

  .target-letter {
    font-size: 60px;
  }

  .finger-hint {
    font-size: 14px;
    padding: 6px 12px;
  }
}

.progress {
  height: 20px;
  background: #eee;
  border-radius: 10px;
  margin: 20px 0;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: #4caf50;
  transition: width 0.3s;
}

.typing-guide {
  margin: 30px auto;
  background: #f8f9fa;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.hands-container {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
  perspective: 800px;
}

.hand {
  display: flex;
  gap: 5px;
  padding: 10px 15px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.1), 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.hand.left-hand {
  transform: rotateY(-10deg);
}

.hand.right-hand {
  transform: rotateY(10deg);
}

.finger-container {
  position: relative;
  height: 120px;
  display: flex;
  align-items: flex-end;
  margin: 0 2px;
}

.finger {
  position: relative;
  z-index: 2;
  width: 40px;
  height: 85px;
  background: linear-gradient(to bottom, #ffdbcb, #f5c8b8);
  border-radius: 20px 20px 10px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: upright;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 -10px 15px rgba(255, 255, 255, 0.5),
    inset 0 2px 5px rgba(255, 255, 255, 0.7);
  transform-origin: bottom center;
  overflow: hidden;
}

/* 添加指甲效果 */
.finger::before {
  content: "";
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 12px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 10px 10px 5px 5px;
  box-shadow: inset 0 -2px 3px rgba(0, 0, 0, 0.1);
}

/* 添加指关节效果 */
.finger::after {
  content: "";
  position: absolute;
  top: 40%;
  left: 0;
  right: 0;
  height: 2px;
  background: rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 0 rgba(0, 0, 0, 0.03);
}

.finger.active {
  background: linear-gradient(to bottom, #e6f7ff, #b3e0ff);
  color: #1976d2;
  transform: translateY(-15px) scale(1.05);
  box-shadow: 0 8px 16px rgba(25, 118, 210, 0.3),
    inset 0 -10px 15px rgba(255, 255, 255, 0.7),
    inset 0 2px 5px rgba(255, 255, 255, 0.9), 0 0 15px rgba(25, 118, 210, 0.5);
}

.finger.active::before {
  background: rgba(255, 255, 255, 0.9);
}

.finger-track {
  position: absolute;
  bottom: 100%;
  left: 50%;
  width: 4px;
  height: 50px;
  background: linear-gradient(to top, rgba(25, 118, 210, 0.5), transparent);
  transform-origin: bottom center;
  animation: moveTrack 0.8s infinite ease-in-out;
  border-radius: 2px;
}

@keyframes moveTrack {
  0% {
    transform: translateX(-50%) translateY(0) scaleY(1);
    opacity: 1;
  }
  50% {
    transform: translateX(-50%) translateY(-20px) scaleY(0.8);
    opacity: 0.5;
  }
  100% {
    transform: translateX(-50%) translateY(-40px) scaleY(0.6);
    opacity: 0;
  }
}

/* 为不同手指设置不同的颜色和大小 */
.finger.pinky {
  width: 32px;
  height: 70px;
  border-bottom: 4px solid #ff4081;
  background: linear-gradient(to bottom, #ffd6cc, #ffbfb3);
}

.finger.ring {
  width: 36px;
  height: 80px;
  border-bottom: 4px solid #7c4dff;
  background: linear-gradient(to bottom, #ffe0cc, #ffc9a3);
}

.finger.middle {
  width: 40px;
  height: 90px;
  border-bottom: 4px solid #00bcd4;
  background: linear-gradient(to bottom, #ffebcc, #ffd699);
}

.finger.index {
  width: 38px;
  height: 85px;
  border-bottom: 4px solid #ffc107;
  background: linear-gradient(to bottom, #fff5cc, #ffeb99);
}

/* 激活状态下的不同手指颜色 */
.finger.pinky.active {
  background: linear-gradient(to bottom, #ffe0f0, #ffb3d9);
  color: #ff4081;
  box-shadow: 0 8px 16px rgba(255, 64, 129, 0.3),
    inset 0 -10px 15px rgba(255, 255, 255, 0.7),
    inset 0 2px 5px rgba(255, 255, 255, 0.9), 0 0 15px rgba(255, 64, 129, 0.5);
}

.finger.ring.active {
  background: linear-gradient(to bottom, #ede0ff, #d1b3ff);
  color: #7c4dff;
  box-shadow: 0 8px 16px rgba(124, 77, 255, 0.3),
    inset 0 -10px 15px rgba(255, 255, 255, 0.7),
    inset 0 2px 5px rgba(255, 255, 255, 0.9), 0 0 15px rgba(124, 77, 255, 0.5);
}

.finger.middle.active {
  background: linear-gradient(to bottom, #e0f7fa, #b2ebf2);
  color: #00bcd4;
  box-shadow: 0 8px 16px rgba(0, 188, 212, 0.3),
    inset 0 -10px 15px rgba(255, 255, 255, 0.7),
    inset 0 2px 5px rgba(255, 255, 255, 0.9), 0 0 15px rgba(0, 188, 212, 0.5);
}

.finger.index.active {
  background: linear-gradient(to bottom, #fff8e1, #ffecb3);
  color: #ffc107;
  box-shadow: 0 8px 16px rgba(255, 193, 7, 0.3),
    inset 0 -10px 15px rgba(255, 255, 255, 0.7),
    inset 0 2px 5px rgba(255, 255, 255, 0.9), 0 0 15px rgba(255, 193, 7, 0.5);
}

/* 手指轨迹颜色 */
.finger.pinky + .finger-track {
  background: linear-gradient(to top, rgba(255, 64, 129, 0.5), transparent);
}
.finger.ring + .finger-track {
  background: linear-gradient(to top, rgba(124, 77, 255, 0.5), transparent);
}
.finger.middle + .finger-track {
  background: linear-gradient(to top, rgba(0, 188, 212, 0.5), transparent);
}
.finger.index + .finger-track {
  background: linear-gradient(to top, rgba(255, 193, 7, 0.5), transparent);
}

.practice-keyboard {
  background: linear-gradient(145deg, #1a1e2e, #232838);
  padding: 25px 30px 35px;
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
    inset 0 2px 10px rgba(255, 255, 255, 0.05), 0 15px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  width: fit-content;
  margin: 0 auto;
  border: 2px solid #0f1117;
  max-width: 100%;
  overflow-x: auto;
  animation: keyboardGlowFloat 6s infinite ease-in-out;
}

/* 添加键盘品牌标志 */
.practice-keyboard::after {
  content: "STEAM.FUN KEYBOARD";
  position: absolute;
  bottom: 8px;
  right: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  padding: 2px 10px;
  border-radius: 10px;
  z-index: 10;
}

/* 添加键盘LED指示灯 */
.practice-keyboard::before {
  content: "";
  position: absolute;
  top: 10px;
  right: 20px;
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  box-shadow: 0 0 10px #4caf50;
  animation: ledBlink 2s infinite alternate;
  z-index: 10;
}

@keyframes ledBlink {
  0% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

.keyboard-frame {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}

/* 添加键盘背光效果 */
.keyboard-frame::before {
  content: "";
  position: absolute;
  top: -15px;
  left: -15px;
  right: -15px;
  bottom: -15px;
  background: radial-gradient(
    ellipse at center,
    rgba(66, 134, 244, 0.15),
    transparent 70%
  );
  z-index: -1;
  border-radius: 20px;
  filter: blur(10px);
  pointer-events: none;
}

.practice-row {
  display: flex;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 6px;
  position: relative;
  white-space: nowrap;
  flex-wrap: nowrap;
}

/* 调整各行的位置 */
.practice-row.number-row {
  padding-left: 0;
}

.practice-row.top-row {
  padding-left: 0;
}

.practice-row.home-row {
  padding-left: 0;
}

.practice-row.bottom-row {
  padding-left: 0;
}

.practice-row.space-row {
  padding-left: 0;
  margin-top: 6px;
}

/* 双键位样式 - 调整为更好的垂直居中效果 */
.practice-key {
  width: 50px;
  height: 50px;
  background: linear-gradient(to bottom, #3a3f52, #2a2e3d);
  color: white;
  border: 1px solid #0f1117;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  font-size: 18px;
  box-shadow: 0 4px 0 #0f1117, 0 4px 10px rgba(0, 0, 0, 0.3),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
}

.practice-key .key-sub {
  font-size: 12px;
  color: #64b5f6;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  margin-bottom: auto;
  position: static;
  transform: none;
}

.practice-key .key-main {
  font-size: 18px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  margin-top: auto;
  position: static;
  transform: none;
}

/* 特殊按键样式 */
.practice-key.special-key {
  background: linear-gradient(to bottom, #2c3143, #1c212f);
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  justify-content: center;
  padding: 0;
}

.practice-key.special-key .key-main {
  margin: 0;
  text-shadow: none;
}

@media (max-width: 992px) {
  .practice-keyboard {
    padding: 20px 25px 30px;
  }

  .practice-key {
    width: 45px;
    height: 45px;
    font-size: 16px;
  }

  .practice-key .key-sub {
    font-size: 10px;
    top: 8px;
  }

  .practice-key .key-main {
    bottom: 8px;
    font-size: 14px;
  }

  .practice-key.special-key.tab,
  .practice-key.special-key.caps {
    width: 75px;
  }

  .practice-key.special-key.backspace {
    width: 70px;
  }

  .practice-key.special-key.enter {
    width: 85px;
  }

  .practice-key.special-key.shift {
    width: 120px;
  }

  .practice-key.special-key.right-shift {
    width: 120px;
  }

  .practice-key.special-key.ctrl,
  .practice-key.special-key.alt,
  .practice-key.special-key.alt-gr,
  .practice-key.special-key.win {
    width: 45px;
  }

  .practice-key.special-key.fn,
  .practice-key.special-key.menu {
    width: 40px;
  }

  .practice-key.special-key.space {
    width: 220px;
  }
}

@media (max-width: 768px) {
  .practice-keyboard {
    padding: 15px 20px 25px;
  }

  .practice-key {
    width: 35px;
    height: 35px;
    font-size: 14px;
    box-shadow: 0 3px 0 #0f1117, 0 3px 8px rgba(0, 0, 0, 0.3);
  }

  .practice-key .key-sub {
    font-size: 8px;
    top: 6px;
  }

  .practice-key .key-main {
    bottom: 6px;
    font-size: 12px;
  }

  .practice-key.special-key {
    font-size: 12px;
  }

  .practice-key.special-key.tab,
  .practice-key.special-key.caps {
    width: 60px;
  }

  .practice-key.special-key.backspace {
    width: 55px;
    font-size: 16px;
  }

  .practice-key.special-key.enter {
    width: 70px;
  }

  .practice-key.special-key.shift {
    width: 80px;
  }

  .practice-key.special-key.right-shift {
    width: 80px;
  }

  .practice-key.special-key.ctrl,
  .practice-key.special-key.alt,
  .practice-key.special-key.alt-gr,
  .practice-key.special-key.win {
    width: 35px;
    font-size: 10px;
  }

  .practice-key.special-key.fn,
  .practice-key.special-key.menu {
    width: 30px;
    font-size: 10px;
  }

  .practice-key.special-key.space {
    width: 170px;
  }

  .practice-row {
    gap: 4px;
    margin-bottom: 4px;
  }

  .practice-row.home-row {
    padding-left: 0;
  }

  .practice-row.bottom-row {
    padding-left: 0;
  }

  .practice-row.space-row {
    margin-top: 4px;
  }

  .single-key {
    font-size: 14px;
  }

  .dual-key-main {
    font-size: 14px;
  }

  .dual-key-sub {
    font-size: 8px;
  }

  .special-key-text {
    font-size: 10px;
  }

  .key-content {
    padding: 4px 0;
  }
}

@media (max-width: 576px) {
  .practice-key {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }

  .practice-key .key-sub {
    font-size: 7px;
  }

  .practice-key .key-main {
    bottom: 5px;
    font-size: 10px;
  }

  .practice-key.special-key.tab,
  .practice-key.special-key.caps {
    width: 55px;
  }

  .practice-key.special-key.backspace {
    width: 50px;
    font-size: 14px;
  }

  .practice-key.special-key.enter {
    width: 60px;
  }

  .practice-key.special-key.shift {
    width: 70px;
  }

  .practice-key.special-key.right-shift {
    width: 70px;
  }

  .practice-key.special-key.ctrl,
  .practice-key.special-key.alt,
  .practice-key.special-key.alt-gr,
  .practice-key.special-key.win {
    width: 30px;
    font-size: 8px;
  }

  .practice-key.special-key.fn,
  .practice-key.special-key.menu {
    width: 25px;
    font-size: 8px;
  }

  .practice-key.special-key.space {
    width: 120px;
  }

  .practice-row.home-row {
    padding-left: 0;
  }

  .practice-row.bottom-row {
    padding-left: 0;
  }

  .single-key {
    font-size: 12px;
  }

  .dual-key-main {
    font-size: 12px;
  }

  .dual-key-sub {
    font-size: 7px;
  }

  .special-key-text {
    font-size: 9px;
  }

  .key-content {
    padding: 3px 0;
  }
}

/* 键盘按键内部光泽效果 */
.practice-key::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), transparent);
  border-radius: 7px 7px 0 0;
  pointer-events: none;
}

.practice-key:hover {
  transform: translateY(2px);
  box-shadow: 0 2px 0 #0f1117, 0 2px 5px rgba(0, 0, 0, 0.3);
}

.practice-key.active {
  background: linear-gradient(to bottom, #1976d2, #1565c0);
  color: white;
  transform: translateY(4px);
  box-shadow: 0 0 0 #0f1117, 0 0 15px rgba(33, 150, 243, 0.5),
    inset 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* 双键位样式 */
.practice-key .key-sub {
  font-size: 12px;
  color: #64b5f6;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.practice-key .key-main {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* 特殊按键样式 */
.practice-key.special-key {
  background: linear-gradient(to bottom, #2c3143, #1c212f);
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.practice-key.special-key .key-main {
  position: static;
  transform: none;
  text-shadow: none;
}

.practice-key.special-key.tab {
  width: 80px;
}

.practice-key.special-key.caps {
  width: 90px;
}

.practice-key.special-key.backspace {
  width: 80px;
  font-size: 20px;
}

.practice-key.special-key.enter {
  width: 100px;
}

.practice-key.special-key.shift {
  width: 120px;
}

.practice-key.special-key.right-shift {
  width: 125px;
}

.practice-key.special-key.ctrl,
.practice-key.special-key.alt,
.practice-key.special-key.win {
  width: 60px;
}

.practice-key.special-key.fn,
.practice-key.special-key.menu {
  width: 50px;
}

.practice-key.special-key.alt-gr {
  width: 50px;
}

.practice-key.special-key.space {
  width: 280px;
  background: linear-gradient(to bottom, #323845, #252a36);
  border-radius: 12px;
  box-shadow: 0 4px 0 #0f1117, 0 4px 10px rgba(0, 0, 0, 0.3),
    inset 0 1px 1px rgba(255, 255, 255, 0.05);
}

/* 基准键位的凸点标记 */
.practice-key.home-key {
  position: relative;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.key-bump {
  position: absolute;
  bottom: 10px;
  width: 8px;
  height: 3px;
  background: #4caf50;
  border-radius: 2px;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
}

/* 按键内容样式 */
.key-main {
  position: relative;
  z-index: 2;
}

/* 按键按下时的动画效果 */
@keyframes keyPress {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
  }
}

.practice-key.active .key-main {
  animation: keyPress 0.15s ease-out;
}

.game-status {
  text-align: center;
  font-size: 1.2em;
  color: #666;
  margin: 20px 0;
}

.level-info {
  text-align: center;
  font-size: 1.2em;
  color: #666;
}

.controls {
  text-align: center;
  margin-top: 20px;
  display: none; /* 隐藏原来的控制按钮区域 */
}

.control-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.game-button {
  padding: 10px 15px;
  font-size: 14px;
  background: linear-gradient(145deg, #f0f2f5, #e6e9ed);
  color: #4a6fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.03),
    0 2px 4px rgba(74, 111, 255, 0.1);
  position: relative;
  overflow: hidden;
  font-weight: 500;
}

.game-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
  transition: all 0.5s;
}

.game-button:hover::before {
  left: 100%;
}

.game-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08), inset 0 -2px 0 rgba(0, 0, 0, 0.03),
    0 4px 6px rgba(74, 111, 255, 0.15);
  background: linear-gradient(145deg, #f5f7fa, #ebeef2);
  color: #3d5cdb;
}

.game-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), inset 0 2px 2px rgba(0, 0, 0, 0.05);
}

.restart-button {
  background: linear-gradient(145deg, #f0f2f5, #e6e9ed);
  color: #4caf50;
}

.restart-button:hover {
  background: linear-gradient(145deg, #f5f7fa, #ebeef2);
  color: #388e3c;
}

.history-btn {
  background: linear-gradient(145deg, #f0f2f5, #e6e9ed);
  color: #9c4aff;
}

.history-btn:hover {
  background: linear-gradient(145deg, #f5f7fa, #ebeef2);
  color: #7e3bd0;
}

.button-icon {
  font-size: 16px;
  margin-right: 4px;
}

.exit-button {
  padding: 10px 15px;
  font-size: 14px;
  background: linear-gradient(145deg, #ef5350, #e57373);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(239, 83, 80, 0.2);
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.exit-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: all 0.5s;
}

.exit-button:hover::before {
  left: 100%;
}

.exit-button:hover {
  background: linear-gradient(145deg, #e53935, #ef5350);
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15), inset 0 -2px 0 rgba(0, 0, 0, 0.1),
    0 4px 6px rgba(239, 83, 80, 0.3);
}

.exit-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 2px 2px rgba(0, 0, 0, 0.1);
}

/* 练习报告弹窗优化 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.report-content {
  background: #ffffff;
  padding: 0;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: bounceIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: none;
  position: relative;
}

@keyframes bounceIn {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  70% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.report-content h2 {
  font-size: 28px;
  margin: 0;
  padding: 25px 0;
  color: #ffffff;
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  border-radius: 24px 24px 0 0;
  position: relative;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.report-content h2::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin: 30px;
  position: relative;
}

.report-stats::before {
  content: "";
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="10" cy="10" r="2" fill="%23f0f0f0"/><circle cx="30" cy="10" r="2" fill="%23f0f0f0"/><circle cx="50" cy="10" r="2" fill="%23f0f0f0"/><circle cx="70" cy="10" r="2" fill="%23f0f0f0"/><circle cx="90" cy="10" r="2" fill="%23f0f0f0"/><circle cx="10" cy="30" r="2" fill="%23f0f0f0"/><circle cx="30" cy="30" r="2" fill="%23f0f0f0"/><circle cx="50" cy="30" r="2" fill="%23f0f0f0"/><circle cx="70" cy="30" r="2" fill="%23f0f0f0"/><circle cx="90" cy="30" r="2" fill="%23f0f0f0"/><circle cx="10" cy="50" r="2" fill="%23f0f0f0"/><circle cx="30" cy="50" r="2" fill="%23f0f0f0"/><circle cx="50" cy="50" r="2" fill="%23f0f0f0"/><circle cx="70" cy="50" r="2" fill="%23f0f0f0"/><circle cx="90" cy="50" r="2" fill="%23f0f0f0"/><circle cx="10" cy="70" r="2" fill="%23f0f0f0"/><circle cx="30" cy="70" r="2" fill="%23f0f0f0"/><circle cx="50" cy="70" r="2" fill="%23f0f0f0"/><circle cx="70" cy="70" r="2" fill="%23f0f0f0"/><circle cx="90" cy="70" r="2" fill="%23f0f0f0"/><circle cx="10" cy="90" r="2" fill="%23f0f0f0"/><circle cx="30" cy="90" r="2" fill="%23f0f0f0"/><circle cx="50" cy="90" r="2" fill="%23f0f0f0"/><circle cx="70" cy="90" r="2" fill="%23f0f0f0"/><circle cx="90" cy="90" r="2" fill="%23f0f0f0"/></svg>');
  z-index: -1;
  opacity: 0.3;
}

.stat-item {
  padding: 20px 15px;
  background: #f8f9ff;
  border-radius: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  border: 2px solid #f0f0f0;
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

@keyframes fadeInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.stat-item:nth-child(1) {
  animation-delay: 0.1s;
}
.stat-item:nth-child(2) {
  animation-delay: 0.2s;
}
.stat-item:nth-child(3) {
  animation-delay: 0.3s;
}
.stat-item:nth-child(4) {
  animation-delay: 0.4s;
}

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #e0e7ff;
  background: #f0f7ff;
}

.stat-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, #4a6fff, #9c4aff);
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 3px 3px 0 0;
}

.stat-item:hover::before {
  opacity: 1;
}

.stat-label {
  font-size: 15px;
  color: #666;
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  font-weight: 600;
  text-align: center;
}

.stat-label::before {
  content: "";
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #4a6fff;
  text-shadow: 0 2px 4px rgba(74, 111, 255, 0.2);
  position: relative;
  display: inline-block;
  text-align: center;
  margin-top: 5px;
}

.report-message {
  margin: 0 30px 30px;
  padding: 25px 20px;
  background: #f0f7ff;
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  border: 2px dashed #e0e7ff;
  animation: fadeIn 0.6s ease-out 0.5s forwards;
  opacity: 0;
  text-align: center;
}

.report-message::before {
  content: "";
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background: #4a6fff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message::after {
  content: "";
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 100px;
  height: 100px;
  background: #9c4aff;
  opacity: 0.1;
  border-radius: 50%;
}

.report-message .result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.report-message img {
  width: 120px;
  height: 120px;
  margin-bottom: 15px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  animation: pulse 2s infinite ease-in-out;
  transform-origin: center;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.report-message p {
  font-size: 20px;
  color: #4a6fff;
  font-weight: 600;
  margin: 10px 0 0;
  padding: 0;
  position: relative;
  display: block;
  text-align: center;
}

.report-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding: 0 30px 30px;
  animation: fadeIn 0.6s ease-out 0.7s forwards;
  opacity: 0;
}

.report-buttons button {
  padding: 12px 25px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  border: none;
  cursor: pointer;
  min-width: 140px;
}

.report-buttons button:first-child {
  background: linear-gradient(135deg, #4a6fff, #9c4aff);
  color: white;
}

.report-buttons button:first-child:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(74, 111, 255, 0.3);
}

.report-buttons button:first-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(74, 111, 255, 0.2);
}

.report-buttons button:last-child {
  background: white;
  color: #666;
  border: 2px solid #eee;
}

.report-buttons button:last-child:hover {
  background: #f8f9ff;
  transform: translateY(-3px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.05);
  border-color: #e0e7ff;
  color: #4a6fff;
}

.report-buttons button:last-child:active {
  transform: translateY(-1px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.03);
}

.report-buttons button::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  transform: translateY(-100%);
  transition: transform 0.6s;
}

.report-buttons button:hover::before {
  transform: translateY(0);
}

.history-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  backdrop-filter: blur(5px);
}

.history-content {
  background: linear-gradient(145deg, #ffffff, #f0f4ff);
  padding: 30px;
  border-radius: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15),
    0 5px 15px rgba(66, 133, 244, 0.1), inset 0 -4px 0 rgba(0, 0, 0, 0.05);
  border: 3px solid rgba(255, 255, 255, 0.7);
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: flex;
  flex-direction: column;
}

@keyframes popIn {
  0% {
    transform: scale(0.9);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  position: relative;
}

.history-title-icon {
  font-size: 32px;
  margin-right: 15px;
  animation: floatIcon 3s infinite ease-in-out;
}

@keyframes floatIcon {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}

.history-content h2 {
  font-size: 28px;
  color: #4a4a4a;
  margin: 0;
  background: linear-gradient(90deg, #7b68ee, #3498db);
  -webkit-background-clip: text;
  background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-weight: 700;
  letter-spacing: 0.5px;
}

.history-decoration {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  z-index: 0;
  opacity: 0.1;
}

.history-decoration.top-left {
  top: -20px;
  left: -20px;
  background: radial-gradient(circle, #ff9ff3, #feca57);
}

.history-decoration.top-right {
  top: -30px;
  right: -30px;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, #48dbfb, #1dd1a1);
}

.history-decoration.bottom-left {
  bottom: -40px;
  left: -20px;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, #ff6b6b, #ee5253);
}

.history-decoration.bottom-right {
  bottom: -30px;
  right: -30px;
  background: radial-gradient(circle, #54a0ff, #2e86de);
}

.history-list {
  margin: 20px 0;
  position: relative;
  z-index: 1;
  overflow-y: auto;
  max-height: calc(80vh - 180px);
  padding-right: 10px;
}

/* 自定义滚动条样式 */
.history-list::-webkit-scrollbar {
  width: 8px;
}

.history-list::-webkit-scrollbar-track {
  background: rgba(240, 240, 255, 0.5);
  border-radius: 10px;
}

.history-list::-webkit-scrollbar-thumb {
  background: rgba(108, 92, 231, 0.3);
  border-radius: 10px;
}

.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(108, 92, 231, 0.5);
}

.history-item {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(230, 230, 250, 0.7);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.history-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
}

.history-item::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #a29bfe, #6c5ce7);
  border-radius: 4px 0 0 4px;
}

.history-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(108, 92, 231, 0.2);
}

.history-date {
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
}

.date-icon {
  margin-right: 5px;
}

.history-badge {
  background: linear-gradient(145deg, #a29bfe, #6c5ce7);
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(108, 92, 231, 0.3);
}

.history-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 8px;
  background: rgba(240, 240, 255, 0.5);
  border-radius: 10px;
  transition: all 0.3s ease;
}

.detail-item:hover {
  background: rgba(230, 230, 250, 0.8);
  transform: translateY(-2px);
}

.detail-icon {
  font-size: 18px;
  margin-right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-item .label {
  color: #666;
  font-size: 13px;
  margin-right: auto;
}

.detail-item .value {
  font-weight: 600;
  color: #6c5ce7;
  font-size: 15px;
  background: linear-gradient(90deg, #6c5ce7, #a29bfe);
  -webkit-background-clip: text;
  background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.no-history {
  text-align: center;
  color: #666;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}

.no-history-icon {
  font-size: 48px;
  margin-bottom: 15px;
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.no-history p {
  font-size: 16px;
  color: #666;
  margin: 0;
}

.no-history-decoration {
  position: absolute;
  bottom: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: radial-gradient(
    circle,
    rgba(108, 92, 231, 0.1),
    rgba(162, 155, 254, 0.05)
  );
  border-radius: 50%;
  z-index: -1;
}

.history-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
  position: sticky;
  bottom: 0;
  padding: 15px 0 5px 0;
  z-index: 10;
}

.history-button {
  padding: 12px 24px;
  font-size: 15px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.history-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: all 0.5s;
}

.history-button:hover::before {
  left: 100%;
}

.history-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
}

.history-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1), inset 0 2px 2px rgba(0, 0, 0, 0.1);
}

.clear-button {
  background: linear-gradient(145deg, #ff6b6b, #ee5253);
  color: white;
}

.clear-button:hover {
  background: linear-gradient(145deg, #ee5253, #ff6b6b);
}

.close-button {
  background: linear-gradient(145deg, #a29bfe, #6c5ce7);
  color: white;
}

.close-button:hover {
  background: linear-gradient(145deg, #6c5ce7, #a29bfe);
}

.guide-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  backdrop-filter: blur(5px);
}

.guide-content {
  background: linear-gradient(145deg, #ffffff, #f0f4ff);
  padding: 40px;
  border-radius: 24px;
  max-width: 900px;
  width: 95%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15),
    0 5px 15px rgba(66, 133, 244, 0.1), inset 0 -4px 0 rgba(0, 0, 0, 0.05);
  border: 3px solid rgba(255, 255, 255, 0.7);
  position: relative;
  overflow: hidden;
  animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.guide-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  position: relative;
}

.guide-title-icon {
  font-size: 32px;
  margin-right: 15px;
  animation: floatIcon 3s infinite ease-in-out;
}

.guide-close-btn {
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 5px;
  transition: all 0.3s ease;
}

.guide-close-btn:hover {
  color: #f44336;
  transform: scale(1.2);
}

.guide-content h2 {
  font-size: 28px;
  color: #4a4a4a;
  margin: 0;
  background: linear-gradient(90deg, #3498db, #2980b9);
  -webkit-background-clip: text;
  background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-weight: 700;
  letter-spacing: 0.5px;
}

.guide-decoration {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  z-index: 0;
  opacity: 0.1;
}

.guide-decoration.top-left {
  top: -20px;
  left: -20px;
  background: radial-gradient(circle, #ff9ff3, #feca57);
}

.guide-decoration.top-right {
  top: -30px;
  right: -30px;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, #48dbfb, #1dd1a1);
}

.guide-decoration.bottom-left {
  bottom: -40px;
  left: -20px;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, #ff6b6b, #ee5253);
}

.guide-decoration.bottom-right {
  bottom: -30px;
  right: -30px;
  background: radial-gradient(circle, #54a0ff, #2e86de);
}

.guide-subtitle {
  font-size: 18px;
  color: #666;
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
}

.guide-keyboard {
  background: #2c3e50;
  padding: 30px 40px;
  border-radius: 15px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2),
    inset 0 2px 5px rgba(255, 255, 255, 0.1);
  position: relative;
  width: fit-content;
  margin: 30px auto;
  transform: perspective(1000px) rotateX(5deg);
  border: 2px solid rgba(255, 255, 255, 0.1);
  z-index: 1;
}

.guide-keyboard::before {
  content: "";
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 5px;
  background: #34495e;
  border-radius: 5px 5px 0 0;
}

.guide-keyboard::after {
  content: "";
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  filter: blur(5px);
}

.finger-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
  position: relative;
  z-index: 1;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.legend-text {
  font-size: 14px;
  color: #666;
}

.guide-row {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  position: relative;
}

/* 第一行（Q-P）位置调整 */
.guide-row:first-child {
  padding-left: 0;
}

/* 第二行（A-L）位置调整 */
.guide-row:nth-child(2) {
  padding-left: 20px;
}

/* 第三行（Z-M）位置调整 */
.guide-row:last-child {
  padding-left: 60px;
}

.guide-key {
  width: 48px;
  height: 48px;
  background: linear-gradient(145deg, #34495e, #2c3e50);
  color: white;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  box-shadow: 0 4px 0 #1a2530, inset 0 1px 1px rgba(255, 255, 255, 0.1);
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.guide-key:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 0 #1a2530, inset 0 1px 1px rgba(255, 255, 255, 0.1);
}

/* 基准键（home row）样式 */
.guide-key.base-key {
  background: linear-gradient(145deg, #3498db, #2980b9);
  box-shadow: 0 4px 0 #1c5a85, inset 0 1px 1px rgba(255, 255, 255, 0.2);
  border-bottom: 3px solid #e74c3c;
}

.guide-key.base-key:hover {
  box-shadow: 0 6px 0 #1c5a85, inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

/* F和J键的突起标记 */
.guide-key.bump::after {
  content: "";
  position: absolute;
  bottom: 8px;
  width: 8px;
  height: 3px;
  background: #e74c3c;
  border-radius: 2px;
  box-shadow: 0 0 5px rgba(231, 76, 60, 0.5);
}

/* 移除非基准行的边框颜色 */
.guide-key[data-finger] {
  border: none; /* 清除所有键的边框 */
}

/* 只为基准行添加颜色标识 */
.guide-row.home-row .guide-key[data-finger="LP"] {
  border-left: 3px solid #ff4081;
}

.guide-row.home-row .guide-key[data-finger="LR"] {
  border-left: 3px solid #7c4dff;
}

.guide-row.home-row .guide-key[data-finger="LM"] {
  border-left: 3px solid #00bcd4;
}

.guide-row.home-row .guide-key[data-finger="LI"] {
  border-left: 3px solid #ffc107;
}

.guide-row.home-row .guide-key[data-finger="RI"] {
  border-right: 3px solid #ffc107;
}

.guide-row.home-row .guide-key[data-finger="RM"] {
  border-right: 3px solid #00bcd4;
}

.guide-row.home-row .guide-key[data-finger="RR"] {
  border-right: 3px solid #7c4dff;
}

.guide-row.home-row .guide-key[data-finger="RP"] {
  border-right: 3px solid #ff4081;
}

/* 添加键盘行标识 */
.guide-row.home-row::before {
  content: "基准行 ——>";
  position: absolute;
  left: -80px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #3498db;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.guide-tips {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 30px 0;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
}

.tip-card {
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border-radius: 16px;
  width: 200px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(230, 230, 250, 0.7);
  transition: all 0.3s ease;
}

.tip-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.05);
}

.tip-icon {
  font-size: 32px;
  margin-bottom: 15px;
  animation: pulse 2s infinite ease-in-out;
}

.tip-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.tip-card p {
  color: #666;
  margin: 5px 0;
}

.guide-buttons {
  margin-top: 30px;
  display: flex;
  justify-content: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}

.guide-button {
  padding: 12px 30px;
  font-size: 16px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.guide-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: all 0.5s;
}

.guide-button:hover::before {
  left: 100%;
}

.guide-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
}

.guide-button:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1), inset 0 2px 2px rgba(0, 0, 0, 0.1);
}

.guide-button.primary {
  background: linear-gradient(145deg, #2ecc71, #27ae60);
  color: white;
}

.guide-button.primary:hover {
  background: linear-gradient(145deg, #27ae60, #2ecc71);
}

.guide-button.secondary {
  background: linear-gradient(145deg, #95a5a6, #7f8c8d);
  color: white;
}

.guide-button.secondary:hover {
  background: linear-gradient(145deg, #7f8c8d, #95a5a6);
}

.guide-btn {
  background: linear-gradient(145deg, #f0f2f5, #e6e9ed);
  color: #212120;
}

.guide-btn:hover {
  background: linear-gradient(145deg, #f5f7fa, #ebeef2);
  color: #006ef5;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scoreChange {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.stat-box.score .stat-value {
  animation: scoreChange 0.3s ease-out;
}

/* 添加进入动画 */
.game-stats {
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translate(50px, -50%);
  }
  to {
    opacity: 1;
    transform: translate(0, -50%);
  }
}

/* 添加响应式布局 */
@media (max-width: 1200px) {
  .typing-game {
    padding-right: 220px;
  }

  .game-sidebar {
    min-width: 160px;
  }
}

@media (max-width: 768px) {
  .typing-game {
    padding-right: 20px;
  }

  .game-sidebar {
    --sidebar-width: 180px;
    top: 10px;
    transform: none;
    border-radius: 12px;
    width: var(--sidebar-width);
    right: 10px;
  }

  .game-sidebar.collapsed {
    width: 24px;
    right: 0;
  }

  .collapse-toggle {
    height: 100%;
    border-radius: 12px 0 0 12px;
  }

  .stat-box {
    padding: 6px 8px;
  }

  .stat-value {
    font-size: 16px;
  }
}

/* 退出按钮样式已在上方定义，此处删除重复定义 */

/* 调整分隔线样式 */
.stats-container .divider {
  margin: 10px 0;
  opacity: 0.2;
}

/* 指导区键盘样式 */
.guide-keyboard {
  background: #2c3e50;
  padding: 30px 40px;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  position: relative;
  width: fit-content;
  margin: 30px auto;
  transform: perspective(1000px) rotateX(5deg);
}

.guide-row {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  position: relative;
}

/* 第一行（Q-P）位置调整 */
.guide-row:first-child {
  padding-left: 0;
}

/* 第二行（A-L）位置调整 */
.guide-row:nth-child(2) {
  padding-left: 20px;
}

/* 第三行（Z-M）位置调整 */
.guide-row:last-child {
  padding-left: 60px;
}

.guide-key {
  width: 48px;
  height: 48px;
  background: #34495e;
  color: white;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  box-shadow: 0 4px 0 #2c3e50;
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

/* 基准键（home row）样式 */
.guide-key.base-key {
  background: #3498db;
  box-shadow: 0 4px 0 #2980b9;
  border-bottom: 3px solid #e74c3c;
}

/* F和J键的突起标记 */
.guide-key.bump::after {
  content: "";
  position: absolute;
  bottom: 8px;
  width: 8px;
  height: 3px;
  background: #e74c3c;
  border-radius: 2px;
  box-shadow: 0 0 5px rgba(231, 76, 60, 0.5);
}

/* 移除非基准行的边框颜色 */
.guide-key[data-finger] {
  border: none; /* 清除所有键的边框 */
}

/* 只为基准行添加颜色标识 */
.guide-row.home-row .guide-key[data-finger="LP"] {
  border-left: 3px solid #ff4081;
}

.guide-row.home-row .guide-key[data-finger="LR"] {
  border-left: 3px solid #7c4dff;
}

.guide-row.home-row .guide-key[data-finger="LM"] {
  border-left: 3px solid #00bcd4;
}

.guide-row.home-row .guide-key[data-finger="LI"] {
  border-left: 3px solid #ffc107;
}

.guide-row.home-row .guide-key[data-finger="RI"] {
  border-right: 3px solid #ffc107;
}

.guide-row.home-row .guide-key[data-finger="RM"] {
  border-right: 3px solid #00bcd4;
}

.guide-row.home-row .guide-key[data-finger="RR"] {
  border-right: 3px solid #7c4dff;
}

.guide-row.home-row .guide-key[data-finger="RP"] {
  border-right: 3px solid #ff4081;
}

/* 添加键盘行标识 */
.guide-row.home-row::before {
  content: "基准行 ——>";
  position: absolute;
  left: -80px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #3498db;
  font-weight: bold;
}

/* 修复键盘按键位置 */
.practice-key {
  position: relative;
}

/* 确保键盘按键内容居中 */
.key-main {
  position: relative;
  z-index: 2;
}

.practice-key.dual-key .key-main,
.practice-key.dual-key .key-sub {
  z-index: 3;
}

/* 添加键盘发光效果 */
.practice-key.dual-key:hover {
  background: linear-gradient(to bottom, #2c3e50, #1c2833);
}

.practice-key.dual-key:hover .key-sub {
  color: #90caf9;
}

/* 调整键盘整体大小 */
.practice-keyboard {
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
}

/* 确保键盘行不会换行 */
.practice-row {
  white-space: nowrap;
  flex-wrap: nowrap;
}

/* 为键盘添加整体边框发光效果 */
@keyframes keyboardGlow {
  0% {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05), 0 0 0 rgba(33, 150, 243, 0.1);
  }
  50% {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05),
      0 0 20px rgba(33, 150, 243, 0.2);
  }
  100% {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05), 0 0 0 rgba(33, 150, 243, 0.1);
  }
}

.practice-keyboard {
  animation: keyboardGlow 4s infinite ease-in-out;
}

/* 为活动按键添加脉冲效果 */
@keyframes keyPulse {
  0% {
    box-shadow: 0 0 0 #0f1117, 0 0 5px rgba(33, 150, 243, 0.5),
      inset 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 0 #0f1117, 0 0 15px rgba(33, 150, 243, 0.8),
      inset 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  100% {
    box-shadow: 0 0 0 #0f1117, 0 0 5px rgba(33, 150, 243, 0.5),
      inset 0 1px 3px rgba(0, 0, 0, 0.3);
  }
}

.practice-key.active {
  animation: keyPulse 1s infinite ease-in-out;
}

/* 为键盘添加更多细节 */
.keyboard-frame {
  position: relative;
}

.keyboard-frame::after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  filter: blur(4px);
}

/* 为按键添加按下时的声音效果样式 */
@keyframes keySound {
  0% {
    opacity: 0.5;
    transform: scale(0.8);
  }
  100% {
    opacity: 0;
    transform: scale(1.2);
  }
}

.practice-key.active::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.2),
    transparent 70%
  );
  border-radius: 8px;
  opacity: 0;
  animation: keySound 0.3s ease-out;
}

/* 为键盘添加微妙的悬浮效果 */
@keyframes keyboardFloat {
  0% {
    transform: perspective(1000px) rotateX(10deg) translateY(0);
  }
  50% {
    transform: perspective(1000px) rotateX(10deg) translateY(-5px);
  }
  100% {
    transform: perspective(1000px) rotateX(10deg) translateY(0);
  }
}

.practice-keyboard {
  animation: keyboardFloat 6s infinite ease-in-out;
}

/* 为键盘添加更精致的边框 */
.practice-keyboard {
  border: 2px solid #0f1117;
  position: relative;
}

.practice-keyboard::before {
  content: "";
  position: absolute;
  top: 10px;
  right: 20px;
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  box-shadow: 0 0 10px #4caf50;
  animation: ledBlink 2s infinite alternate;
  z-index: 10;
}

/* 添加第二个LED指示灯 */
.practice-keyboard::after {
  content: "STEAM.FUN KEYBOARD";
  position: absolute;
  bottom: 8px;
  right: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  padding: 2px 10px;
  border-radius: 10px;
  z-index: 10;
}

/* 添加第二个LED指示灯 */
.practice-keyboard .second-led {
  position: absolute;
  top: 10px;
  right: 40px;
  width: 8px;
  height: 8px;
  background: #ff5722;
  border-radius: 50%;
  box-shadow: 0 0 10px #ff5722;
  animation: ledBlink 3s infinite alternate-reverse;
}

/* 添加第三个LED指示灯 */
.practice-keyboard .third-led {
  position: absolute;
  top: 10px;
  right: 60px;
  width: 8px;
  height: 8px;
  background: #2196f3;
  border-radius: 50%;
  box-shadow: 0 0 10px #2196f3;
  animation: ledBlink 2.5s infinite alternate;
}

/* 为键盘添加整体边框发光效果和悬浮效果 */
@keyframes keyboardGlowFloat {
  0% {
    transform: perspective(1000px) rotateX(10deg) translateY(0);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05), 0 0 0 rgba(33, 150, 243, 0.1);
  }
  50% {
    transform: perspective(1000px) rotateX(10deg) translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05),
      0 0 20px rgba(33, 150, 243, 0.2);
  }
  100% {
    transform: perspective(1000px) rotateX(10deg) translateY(0);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5),
      inset 0 2px 10px rgba(255, 255, 255, 0.05), 0 0 0 rgba(33, 150, 243, 0.1);
  }
}

.practice-keyboard {
  animation: keyboardGlowFloat 6s infinite ease-in-out;
  transform: none !important;
}

/* 修复按键动画冲突 */
.practice-key.active {
  animation: keyPulse 1s infinite ease-in-out;
  transform: translateY(4px) !important;
}

/* 修复媒体查询中的键盘变换 */
@media (max-width: 1200px),
  (max-width: 992px),
  (max-width: 768px),
  (max-width: 576px) {
  .practice-keyboard {
    transform: none !important;
  }
}

/* 双键位内容容器样式 */
.key-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  padding: 6px 0;
}

.key-content .key-sub {
  font-size: 12px;
  color: #64b5f6;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.key-content .key-main {
  font-size: 18px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* 特殊按键样式 */
.practice-key.special-key {
  background: linear-gradient(to bottom, #2c3143, #1c212f);
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.practice-key.special-key .key-main {
  text-shadow: none;
}

@media (max-width: 992px) {
  .key-content {
    padding: 5px 0;
  }

  .key-content .key-sub {
    font-size: 10px;
  }

  .key-content .key-main {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .key-content {
    padding: 4px 0;
  }

  .key-content .key-sub {
    font-size: 8px;
  }

  .key-content .key-main {
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .key-content {
    padding: 3px 0;
  }

  .key-content .key-sub {
    font-size: 7px;
  }

  .key-content .key-main {
    font-size: 10px;
  }
}

/* 单键位样式 */
.single-key {
  font-size: 18px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  margin-top: 15px;
}

/* 双键位样式 */
.dual-key-sub {
  font-size: 12px;
  color: #64b5f6;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.dual-key-main {
  font-size: 18px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* 特殊键位样式 */
.special-key-text {
  font-size: 14px;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* 键位内容容器样式 */
.key-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  padding: 6px 0;
}

@media (max-width: 992px) {
  .single-key {
    font-size: 16px;
  }

  .dual-key-main {
    font-size: 16px;
  }

  .dual-key-sub {
    font-size: 10px;
  }

  .special-key-text {
    font-size: 12px;
  }

  .key-content {
    padding: 5px 0;
  }
}

@media (max-width: 768px) {
  .single-key {
    font-size: 14px;
  }

  .dual-key-main {
    font-size: 14px;
  }

  .dual-key-sub {
    font-size: 8px;
  }

  .special-key-text {
    font-size: 10px;
  }

  .key-content {
    padding: 4px 0;
  }
}

@media (max-width: 576px) {
  .single-key {
    font-size: 12px;
  }

  .dual-key-main {
    font-size: 12px;
  }

  .dual-key-sub {
    font-size: 7px;
  }

  .special-key-text {
    font-size: 9px;
  }

  .key-content {
    padding: 3px 0;
  }
}

/* 手指悬停效果 */
.finger:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15),
    inset 0 -10px 15px rgba(255, 255, 255, 0.5),
    inset 0 2px 5px rgba(255, 255, 255, 0.7);
}

/* 手指文字样式优化 */
.finger {
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
</style>
